import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Source: docs/use_case_video_series.md EP03 — "The Family History Puzzle".

export const apoeLena: Scenario = {
  slug: 'apoe-lena',
  series: 'APOE & Brain Health',
  title: "Her mother's diagnosis raised the question",
  socialHook:
    "Lena, 52: her mother was diagnosed with Alzheimer's. She came looking for a risk number. The useful answer was a set of clues she could act on.",
  meta: {
    description:
      "A fictional case study: Lena's mother has Alzheimer's. Her APOE result explains part of her risk, but the bigger story is how cholesterol, sleep, movement, and vitamin D change the picture.",
  },

  persona: {
    name: 'Lena',
    age: 52,
    sex: 'Female',
    ancestry: 'Northern European',
    occupation: 'Secondary school teacher',
    chiefConcern:
      "Her mother was diagnosed with Alzheimer's six months ago. Lena wants to know whether the same thing is waiting for her — and what she can do now while she feels well.",
    familyHistory:
      "Mother: Alzheimer's disease, diagnosed age 74. No other known neurological conditions.",
  },

  clinicalPicture: {
    symptoms: [
      'No cognitive symptoms — she feels well',
      'Occasional poor sleep (wakes at 3–4am, struggles to get back to sleep)',
      'Slightly elevated blood pressure at last check',
      'Sedentary lifestyle — desk job, minimal formal exercise',
    ],
    labs: [
      { name: 'LDL ("bad") Cholesterol', value: '147 mg/dL', refRange: '<116 mg/dL', flag: 'high' },
      { name: 'Vitamin D', value: '15 ng/mL', refRange: '20–50 ng/mL', flag: 'low' },
    ],
    medications: ['No prescription medications'],
    supplements: ['Occasional multivitamin'],
    lifestyle: [
      'Mostly sedentary — walks occasionally, no regular exercise routine',
      'Sleep: 6–7 hours, frequently disrupted',
      'Diet: balanced but high in processed foods and refined carbs',
      'Non-smoker, alcohol 3–4 drinks/week',
    ],
  },

  genetics: [
    {
      gene: 'APOE',
      variant: 'ε3/ε4',
      rsid: 'rs429358 / rs7412',
      genotype: 'One copy of ε4',
      plainMeaning:
        "Lena has one copy of a risk version that makes brain health more sensitive to cholesterol, sleep, blood pressure, and exercise. APOE ε4 raises risk, but it also points to what matters most.",
      technicalDetail:
        "APOE ε4/ε3 is the most common elevated-risk APOE pattern, carried by roughly 1 in 4 people of European ancestry. One ε4 copy raises lifetime risk of late-onset Alzheimer's approximately 3–4× compared to the most common ε3/ε3 type. APOE also governs cholesterol metabolism in the brain and clearance of amyloid proteins during sleep — processes that ε4 handles less efficiently than ε3.",
    },
    {
      gene: 'APOE',
      variant: 'ε3 (second copy)',
      rsid: 'rs7412',
      genotype: 'ε3/ε4 overall',
      plainMeaning:
        "Her second copy is the common version, so this is elevated risk, not the highest-risk pattern. This is also the group where prevention habits have some of the clearest evidence.",
      technicalDetail:
        "The ε3 allele is the most common and considered the neutral reference variant. The ε3/ε4 combination is heterozygous — carrying the ε4 risk without the compounding effect of ε4/ε4 (which raises risk 8–12×). Crucially, the modifiable risk factor literature for Alzheimer's — cardiovascular, sleep, exercise — has strong evidence specifically in ε4 heterozygotes, making this the pattern where behavior most demonstrably moves the needle.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: 'The phone call that made everything feel urgent',
      body: "Six months ago, Lena's mother — sharp, independent, a retired librarian — was diagnosed with Alzheimer's. Lena is 52, feels well, and has no memory problems. But one question keeps returning: is this coming for me too? She uploads her 23andMe file and logs her health records. She expects a number. What she gets is a map.",
    },
    {
      type: 'narrative',
      heading: "The ordinary clues suddenly mattered",
      body: "Lena's blood work is not alarming, but it is not clean either. Her LDL cholesterol is above where it should be. Her blood pressure is creeping up. Her vitamin D is low. She is not sleeping well and does not exercise regularly. None of this sounded urgent at her last family doctor visit. In the context of APOE ε4, each clue carries more weight.",
    },
    {
      type: 'reveal',
      heading: 'Her risk was real, but not fixed',
      body: "Lena carries one copy of APOE ε4, which roughly triples baseline risk of late-onset Alzheimer's compared with the most common pattern. About 1 in 4 people of European ancestry carry at least one ε4 copy. The important part is not just the risk number. APOE also affects how the brain handles cholesterol, clears waste proteins during sleep, and responds to cardiovascular stress.",
    },
    {
      type: 'reveal',
      heading: "The hopeful part was in the other clues",
      body: "The risk is not the whole story. Her elevated LDL matters more because APOE ε4 affects cholesterol handling in the brain. Her disrupted sleep matters more because deep sleep is when the brain clears proteins linked with Alzheimer's, and ε4 carriers clear them less efficiently. Her blood pressure matters more because vascular health and cognitive decline are closely linked. The good news is timing: these are warning lights, not established damage.",
    },
    {
      type: 'action',
      heading: "Five moves that make the risk actionable",
      items: [
        "Bring LDL and blood pressure into healthy ranges. For APOE ε4 carriers, cardiovascular control is one of the strongest brain-protection conversations to have with a family doctor.",
        "Make sleep a medical priority: aim for 7–8 hours with consistent timing. Deep sleep is when the brain clears amyloid, so disrupted sleep is not a small detail here.",
        "Start regular aerobic exercise: 150 minutes per week of moderate activity, with brisk walking counting. Exercise supports brain cell health and lowers vascular risk.",
        "Correct vitamin D deficiency and recheck in 3 months. Her level is well below the recommended range, and this is a straightforward fix to track.",
        "Shift toward a Mediterranean-style diet: less processed food and refined carbs, more olive oil, fish, vegetables, and whole grains. The goal is consistency, not perfection.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'riskdial' as const,
      heading: "How the clues change her picture",
      caption:
        "This is illustrative — not a medical risk calculator. It shows how Lena's modifiable clues layer on top of APOE ε4, and how addressing each one can move the picture.",
      baselineLabel: 'APOE ε3/ε4 carrier baseline',
      baseline: 72,
      levers: [
        {
          label: 'Cardiovascular risk controlled',
          delta: -14,
          description: 'LDL and BP into healthy ranges — the strongest modifiable lever for ε4 carriers.',
        },
        {
          label: 'Sleep improved to 7–8 hrs',
          delta: -10,
          description: 'Consistent deep sleep restores amyloid clearance — critical for ε4 carriers who clear more slowly.',
        },
        {
          label: '150 min/week aerobic exercise',
          delta: -9,
          description: 'Aerobic exercise raises BDNF and reduces vascular risk. Best-evidenced behavioral intervention.',
        },
        {
          label: 'Vitamin D optimized',
          delta: -4,
          description: 'Correcting deficiency removes an independent risk factor for cognitive decline.',
        },
        {
          label: 'Mediterranean-style diet',
          delta: -6,
          description: 'Reduces LDL, inflammation, and vascular burden — all relevant for ε4 carriers.',
        },
      ],
    },
    {
      type: 'streak' as const,
      heading: "Lena's 8-week exercise goal",
      goalLabel: '150 min/week — aerobic exercise',
      caption:
        "Lena set a goal in Livewello: 30 minutes of brisk walking, 5 days a week. Week 1 was rough. By week 5 she had her first full week. By week 8 the habit had taken hold — and her check-in notes show the shift.",
      weeks: [
        { weekLabel: 'Week 1', days: [true, false, false, true, false, false, false], note: "Only managed two days. Work was hectic and I kept putting it off." },
        { weekLabel: 'Week 2', days: [true, true, false, false, true, false, true], note: "Better. Four days but still not consistent." },
        { weekLabel: 'Week 3', days: [false, true, true, false, true, true, false], note: "Missed Monday and Friday but the middle of the week felt easier." },
        { weekLabel: 'Week 4', days: [true, false, true, true, false, true, true], note: "Five days — closest yet. Starting to feel different when I skip." },
        { weekLabel: 'Week 5', days: [true, true, true, true, true, false, false], note: "First full work-week streak! Weekends are still my weak spot." },
        { weekLabel: 'Week 6', days: [true, true, false, true, true, true, false], note: "Five days. Starting to feel genuinely routine." },
        { weekLabel: 'Week 7', days: [true, true, true, false, true, true, true], note: "Six days. Missed Wednesday but got a weekend walk in for the first time." },
        { weekLabel: 'Week 8', days: [true, true, true, true, true, true, false], note: "Six days again. Sleep has improved noticeably. BP recheck next week." },
      ],
    },
  ],

  cta: {
    namedPrompt: 'apoe4',
    label: 'Explore your APOE genetics in Coach',
  },
}
